import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Eye, LogOut, Clock, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { roleLabel, secondsLeft } from '../../lib/viewAs';
import { Switch } from '../ui/switch';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';

const AMBER = '#F5A623';
const INK = '#1E2530';
const WARN_AT = 5 * 60;

const mmss = (s) => `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;

/** Fixed amber bar shown on every page while an admin is viewing the app as another user. */
export function ImpersonationBanner({ scope = 'global' }) {
  const { viewAs, endViewAs, setViewAsEdits, extendViewAs } = useAuth();
  const location = useLocation();
  const [left, setLeft] = useState(() => secondsLeft(viewAs));
  const [reasonOpen, setReasonOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const warnedRef = useRef(false);
  const expiredRef = useRef(false);

  useEffect(() => {
    if (!viewAs) return undefined;
    setLeft(secondsLeft(viewAs));
    const t = setInterval(() => setLeft(secondsLeft(viewAs)), 1000);
    return () => clearInterval(t);
  }, [viewAs]);

  useEffect(() => {
    if (!viewAs) return;
    if (left <= 0 && !expiredRef.current) { expiredRef.current = true; endViewAs('expired'); return; }
    if (left > 0 && left <= WARN_AT && !warnedRef.current) {
      warnedRef.current = true;
      toast.warning('Impersonation ends in 5 minutes', { description: viewAs.extensions ? 'This session has already been extended.' : 'Use "Extend 30 min" on the banner to keep going.' });
    }
    if (left > WARN_AT) warnedRef.current = false;
  }, [left, viewAs, endViewAs]);

  if (!viewAs) return null;
  // Portal and diary shells pin their own copy inside their sticky headers.
  const p = location.pathname;
  if (scope === 'global' && (p.startsWith('/portal') || p.startsWith('/diary'))) return null;

  const warn = left <= WARN_AT;
  const canExtend = warn && !viewAs.extensions;

  const toggleEdits = async (next) => {
    if (next) { setReason(''); setReasonOpen(true); return; }
    setBusy(true);
    try { await setViewAsEdits(false); toast.message('Back to read-only'); } catch (e) { toast.error(e.response?.data?.detail || 'Could not update'); } finally { setBusy(false); }
  };
  const confirmEdits = async () => {
    if (!reason.trim()) return;
    setBusy(true);
    try { await setViewAsEdits(true, reason.trim()); setReasonOpen(false); toast.warning('Edits allowed — changes are saved as you, on their behalf'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not allow edits'); } finally { setBusy(false); }
  };
  const extend = async () => {
    setBusy(true);
    try { await extendViewAs(); warnedRef.current = false; toast.success('Extended by 30 minutes'); } catch (e) { toast.error(e.response?.data?.detail || 'Could not extend'); } finally { setBusy(false); }
  };

  return (
    <>
      <div
        className={`${scope === 'global' ? 'sticky top-0 z-[70]' : ''} w-full flex items-center justify-center gap-x-4 gap-y-1 px-4 py-1.5 flex-wrap`}
        style={{ background: AMBER, color: INK, boxShadow: '0 1px 3px rgba(0,0,0,0.2)', paddingTop: scope === 'global' ? 'calc(0.375rem + env(safe-area-inset-top, 0px))' : undefined }}
        data-testid="impersonation-banner"
        data-imp-banner
        role="status"
        aria-live="polite"
      >
        <span className="inline-flex items-center gap-2 text-[13px] font-semibold">
          <Eye className="w-4 h-4" strokeWidth={2.5} />
          Viewing as <span data-testid="impersonation-banner-name">{viewAs.acting_name}</span>
          <span className="font-normal">({roleLabel(viewAs)})</span>
          <span className="opacity-60">—</span>
          <span data-testid="impersonation-banner-mode">{viewAs.allow_edits ? 'edits allowed' : 'read-only'}</span>
        </span>

        <span className={`inline-flex items-center gap-1.5 text-xs font-semibold tabular-nums px-2 py-0.5 rounded-full ${warn ? 'bg-[#B91C1C] text-white' : 'bg-black/10'}`} data-testid="impersonation-banner-timer" title="Session ends in">
          <Clock className="w-3.5 h-3.5" /> {mmss(left)}
          {canExtend && (
            <button type="button" onClick={extend} disabled={busy} className="ml-1 underline underline-offset-2 hover:opacity-80" data-testid="impersonation-extend-btn">Extend 30 min</button>
          )}
        </span>

        <label className="inline-flex items-center gap-2 text-xs font-semibold cursor-pointer" data-testid="impersonation-edits-toggle-label">
          <Pencil className="w-3.5 h-3.5" /> Allow edits
          <Switch checked={!!viewAs.allow_edits} onCheckedChange={toggleEdits} disabled={busy} data-testid="impersonation-edits-toggle" className="data-[state=checked]:bg-[#1E2530] data-[state=unchecked]:bg-black/25" />
        </label>

        <button
          type="button"
          onClick={() => endViewAs('exit')}
          className="inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1 rounded-md transition-opacity hover:opacity-90"
          style={{ background: INK, color: AMBER }}
          data-testid="impersonation-exit-btn"
        >
          <LogOut className="w-3.5 h-3.5" /> Exit
        </button>
      </div>

      <Dialog open={reasonOpen} onOpenChange={(o) => !busy && setReasonOpen(o)}>
        <DialogContent className="bg-[#2A3240] border-white/10 text-white sm:max-w-md" data-imp-banner data-testid="impersonation-reason-dialog">
          <DialogHeader>
            <DialogTitle>Allow edits while viewing as {viewAs.acting_name}?</DialogTitle>
            <DialogDescription className="text-gray-400">
              Anything you save will be recorded as <strong className="text-gray-200">{viewAs.real_name} (as {viewAs.acting_name})</strong>. No emails or alerts go out to anyone during this session. A one-line reason is kept in the impersonation log.
            </DialogDescription>
          </DialogHeader>
          <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason, e.g. fixing Dave's timesheet for 12/10" autoFocus maxLength={200}
            onKeyDown={(e) => e.key === 'Enter' && confirmEdits()} className="bg-[#1E2530] border-white/10 text-white" data-testid="impersonation-reason-input" />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setReasonOpen(false)} disabled={busy} className="text-gray-300" data-testid="impersonation-reason-cancel">Cancel</Button>
            <Button onClick={confirmEdits} disabled={busy || !reason.trim()} className="bg-[#F5A623] text-[#1E2530] hover:bg-[#D98E1D]" data-testid="impersonation-reason-confirm">Allow edits</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
